import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import userFallback from "../assets/images/order/delivary-partner.png";
import terms from "../assets/images/navbar/terms.png";
import star from "../assets/images/navbar/star.png";
import orangeStar from "../assets/images/navbar/orange-star.png";
import greenTick from "../assets/images/order/green-tick.png";
import redCancel from "../assets/images/order/red-cancel.png";
import { restaurantOrderService, restaurantService } from "../services";
import { useRestaurant } from "../context/RestaurantContext";
import { Order } from "../utils/types";
import { getCustomerName, getOrderRating, timeAgo } from "../utils/analytics";

type ReviewsAndComplainProps = {
  setIsReviewsAndComplainOpen?: (open: boolean) => void;
};

type ReviewItem = {
  id: string;
  userName: string;
  userImage: string;
  timestamp: string;
  rating: number;
  amount: number;
};

type ComplaintItem = {
  id: string;
  title: string;
  orderNumber: string;
  location: string;
  time: string;
  status: string;
  statusColor: string;
  userName: string;
  userImage: string;
  items: { name: string; quantity: number; price: number }[];
};

const ReviewsAndComplain = ({
  setIsReviewsAndComplainOpen,
}: ReviewsAndComplainProps) => {
  const { restaurantProfile } = useRestaurant();
  const [activeTab, setActiveTab] = useState<"reviews" | "complaints">("reviews");
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [complaints, setComplaints] = useState<ComplaintItem[]>([]);
  const [loading, setLoading] = useState(true);

  const handleClose = () => setIsReviewsAndComplainOpen?.(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const restaurantId = restaurantProfile?._id || restaurantProfile?.restaurantId;
        let mappedReviews: ReviewItem[] = [];

        if (restaurantId) {
          try {
            const ratingsRes: any = await restaurantService.getReviews(restaurantId);
            const list = ratingsRes?.data?.reviews || ratingsRes?.data || [];
            if (Array.isArray(list) && list.length > 0) {
              mappedReviews = list.map((item: any, index: number) => ({
                id: item._id || String(index),
                userName:
                  item.userName ||
                  [item.user?.firstName, item.user?.lastName].filter(Boolean).join(" ") ||
                  "Customer",
                userImage: item.user?.image || item.userImage || userFallback,
                timestamp: timeAgo(item.createdAt),
                rating: item.rating || 0,
                amount: item.orderAmount || item.totalAmount || 0,
              }));
            }
          } catch (error) {
            console.warn("Reviews endpoint unavailable, using delivered orders");
          }
        }

        const [deliveredRes, cancelledRes]: any[] = await Promise.all([
          restaurantOrderService.getOrders({ page: 1, limit: 30, orderStatus: "delivered" }),
          restaurantOrderService.getOrders({ page: 1, limit: 30, orderStatus: "cancelled" }),
        ]);

        const delivered: Order[] = deliveredRes?.data || [];
        const cancelled: Order[] = cancelledRes?.data || [];

        if (mappedReviews.length === 0) {
          mappedReviews = delivered
            .filter((order) => getOrderRating(order) > 0)
            .map((order) => ({
              id: order._id,
              userName: getCustomerName(order),
              userImage: order.user?.image || userFallback,
              timestamp: `Delivered ${timeAgo(order.deliveredAt || order.updatedAt)}`,
              rating: getOrderRating(order),
              amount: order.totalAmount || 0,
            }));
        }

        setReviews(mappedReviews);
        setComplaints(
          cancelled.map((order) => ({
            id: order._id,
            title: order.cancellationReason || "Order cancelled",
            orderNumber: order._id.slice(-6).toUpperCase(),
            location: order.deliveryAddress?.addressLine1 || order.deliveryAddress?.city || "",
            time: new Date(order.createdAt).toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
            }),
            status: "Cancelled",
            statusColor: "bg-[#8E8E93]",
            userName: getCustomerName(order),
            userImage: order.user?.image || userFallback,
            items: (order.items || []).map((item) => ({
              name: item.dishName,
              quantity: item.quantity,
              price: item.totalItemPrice || item.price,
            })),
          }))
        );
      } catch (error) {
        console.error("Failed to load reviews:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [restaurantProfile]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]" onClick={handleClose}>
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-900 rounded-l-[20px] overflow-y-auto"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full flex justify-between items-center p-6 pb-4">
          <div className="flex items-center gap-4">
            <img src={arrowLeft} alt="back" className="w-[36px] h-[36px] rounded-full border cursor-pointer p-2" onClick={handleClose} />
            <h1 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[20px] font-semibold">
              Reviews and Complaints
            </h1>
          </div>
          <img src={cancel} alt="close" className="w-[36px] h-[36px] p-2 rounded-full border cursor-pointer" onClick={handleClose} />
        </div>

        <div className="w-full flex border-b dark:border-gray-700 px-6">
          <button
            className={`flex-1 pb-3 font-Urbanist text-[16px] font-semibold ${
              activeTab === "reviews" ? "text-[#FF6B35] border-b-2 border-[#FF6B35]" : "text-[#8E8E93]"
            }`}
            onClick={() => setActiveTab("reviews")}
          >
            Reviews
          </button>
          <button
            className={`flex-1 pb-3 font-Urbanist text-[16px] font-semibold ${
              activeTab === "complaints" ? "text-[#FF6B35] border-b-2 border-[#FF6B35]" : "text-[#8E8E93]"
            }`}
            onClick={() => setActiveTab("complaints")}
          >
            Complaints
          </button>
        </div>

        <div className="w-full p-6 space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8A00]"></div>
            </div>
          ) : activeTab === "reviews" ? (
            reviews.length === 0 ? (
              <p className="text-center text-gray-500 py-10">No reviews yet</p>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="w-full p-4 rounded-[12px] border dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="flex justify-between items-center pb-[10px] border-b-2 dark:border-gray-700">
                    <div className="flex gap-2">
                      <img src={review.userImage} alt={review.userName} className="w-[48px] h-[48px] rounded-[10px] object-cover" />
                      <div>
                        <h3 className="text-[#040404] dark:text-gray-200 font-semibold">{review.userName}</h3>
                        <p className="text-[#8E8E93] text-[12px]">{review.timestamp}</p>
                      </div>
                    </div>
                    <span className="font-semibold dark:text-gray-200">₹{Number(review.amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[12px] dark:text-gray-200">Rated</span>
                      <div className="flex gap-1">
                        {Array.from({ length: 5 }, (_, index) => (
                          <img
                            key={index}
                            src={index < review.rating ? orangeStar : star}
                            alt="star"
                            className="h-[14px] w-[14px]"
                          />
                        ))}
                      </div>
                    </div>
                    <button className="mt-3 flex items-center gap-1 text-[#8E8E93]">
                      <img src={terms} alt="Terms" className="h-[10px] w-[10px]" />
                      <span className="text-[#565656] text-[12px]">All Details</span>
                    </button>
                  </div>
                </div>
              ))
            )
          ) : complaints.length === 0 ? (
            <p className="text-center text-gray-500 py-10">No complaints yet</p>
          ) : (
            complaints.map((complaint) => (
              <div key={complaint.id} className="w-full p-4 rounded-[12px] border dark:border-gray-700 bg-white dark:bg-gray-800 space-y-[10px]">
                <div className="flex justify-between items-start pb-[10px] border-b-2 dark:border-gray-700">
                  <div>
                    <h3 className="text-[#050505] dark:text-gray-200 font-medium">{complaint.title}</h3>
                    <p className="text-[#8E8E93] text-[10px]">
                      Order #{complaint.orderNumber} - {complaint.location} - {complaint.time}
                    </p>
                  </div>
                  <span className={`px-[6px] py-[5px] rounded-[8px] text-white text-[11px] font-semibold ${complaint.statusColor}`}>
                    {complaint.status}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-[10px] border-b-2 dark:border-gray-700">
                  <div className="flex gap-2">
                    <img src={complaint.userImage} alt={complaint.userName} className="w-[48px] h-[48px] rounded-[10px] object-cover" />
                    <div>
                      <h3 className="font-semibold dark:text-gray-200">{complaint.userName}</h3>
                      <p className="text-[#8E8E93] text-[12px]">{complaint.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 px-[9px] py-[7.5px] border rounded-[8px] bg-[#F5F5F5] dark:bg-gray-700">
                    <img src={greenTick} alt="verified" className="h-[20px] w-[20px]" />
                    <span className="text-[12px]">Verified Customer</span>
                  </div>
                </div>
                <div className="space-y-1 pb-[10px] border-b-2 dark:border-gray-700">
                  {complaint.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm dark:text-gray-200">{item.quantity} x {item.name}</span>
                      <span className="font-semibold dark:text-gray-200">₹{Number(item.price).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex w-full gap-3">
                  <button
                    onClick={() => toast.info("Refunds are handled from the Orders screen for this order.")}
                    className="w-1/2 flex justify-center items-center gap-2 py-2 rounded-[8px] border bg-[#F5F5F5] dark:bg-gray-700 text-[#34C759] font-semibold"
                  >
                    <img src={greenTick} alt="refund" className="h-[24px] w-[24px]" />
                    Give Refund
                  </button>
                  <button
                    onClick={() => toast.success("Complaint dismissed locally")}
                    className="w-1/2 flex justify-center items-center gap-2 py-2 rounded-[8px] border bg-[#F5F5F5] dark:bg-gray-700 text-[#FF3B30] font-semibold"
                  >
                    <img src={redCancel} alt="dismiss" className="h-[24px] w-[24px]" />
                    Dismiss
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewsAndComplain;
